const parseMaybeJson = (value, fallback = null) => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
};

const normalizeId = (value) => {
  if (value == null || value === '') return '';
  return String(value).trim();
};

const compactIds = (values) => new Set(values.map(normalizeId).filter(Boolean));

const arrayFrom = (value) => {
  const parsed = parseMaybeJson(value, value);
  if (Array.isArray(parsed)) return parsed;
  return parsed ? [parsed] : [];
};

export const normalizeProofPoId = (proof = {}) => normalizeId(
  proof.poId ??
  proof.purchaseOrderId ??
  proof.purchase_order_id ??
  proof.POId ??
  proof.po_id
);

const getSupplierInfo = (poData = {}) => parseMaybeJson(poData?.supplierInfo, {}) || {};

const getPoIds = (poData = {}) => compactIds([
  poData.poId,
  poData.id,
  poData.purchaseOrderId,
  poData.purchase_order_id,
  poData.POId,
  poData.po_id,
]);

const getPoNumbers = (poData = {}) => compactIds([
  poData.poNumber,
  poData.purchaseOrderNumber,
  poData.purchase_order_number,
]);

const getDecoratorIds = (poData = {}) => {
  const supplierInfo = getSupplierInfo(poData);
  return compactIds([
    supplierInfo.decoratorId,
    supplierInfo.DecoratorId,
    supplierInfo.decorator_id,
    poData.decoratorId,
    poData.DecoratorId,
    poData.decorator_id,
  ]);
};

const collectDecorations = (poData = {}) => {
  const supplierInfo = getSupplierInfo(poData);
  const approvedLines = Array.isArray(supplierInfo.approvedLines) ? supplierInfo.approvedLines : [];
  const items = Array.isArray(poData.items) ? poData.items : [];
  const decorations = [];

  items.forEach((item) => {
    arrayFrom(item?.decorations || item?.Decorations).forEach((deco) => decorations.push(deco));
  });

  approvedLines.forEach((ref) => {
    arrayFrom(ref?.line?.Decorations || ref?.line?.decorations || ref?.Decorations || ref?.decorations).forEach((deco) => decorations.push(deco));
  });

  return decorations.filter(Boolean);
};

const getDecorationIds = (poData = {}) => compactIds(
  collectDecorations(poData).flatMap((deco) => [
    deco.id,
    deco.decorationId,
    deco.DecorationId,
    deco.decoration_id,
    deco.locationId,
    deco.location_id,
  ])
);

const getLineIds = (poData = {}) => {
  const supplierInfo = getSupplierInfo(poData);
  const approvedLines = Array.isArray(supplierInfo.approvedLines) ? supplierInfo.approvedLines : [];
  const items = Array.isArray(poData.items) ? poData.items : [];
  return compactIds([
    ...items.flatMap((item) => [item?.id, item?.OrderItemId, item?.orderItemId, item?.ProductId, item?.productId]),
    ...approvedLines.flatMap((ref) => [ref?.orderItemId, ref?.OrderItemId, ref?.productId, ref?.ProductId, ref?.line?.id]),
  ]);
};

const getProofDecorationIds = (proof = {}) => compactIds([
  proof.decorationId,
  proof.DecorationId,
  proof.decoration_id,
  proof.locationId,
  proof.location_id,
  proof.decoration?.id,
  proof.decoration?.decorationId,
]);

const getProofDecoratorIds = (proof = {}) => compactIds([
  proof.decoratorId,
  proof.DecoratorId,
  proof.decorator_id,
  proof.decoration?.decoratorId,
  proof.decoration?.DecoratorId,
  proof.decoration?.DecoratorDetails?.decoratorId,
]);

const getProofLineIds = (proof = {}) => compactIds([
  proof.orderItemId,
  proof.OrderItemId,
  proof.order_item_id,
  proof.productId,
  proof.ProductId,
  proof.product_id,
  proof.lineId,
  proof.line_id,
]);

const setsIntersect = (a, b) => [...a].some((value) => b.has(value));

export const hasUploadedProofFile = (proof = {}) => {
  const filePath = normalizeId(
    proof.filePath ||
    proof.proofFilePath ||
    proof.sourceFilePath ||
    proof.url ||
    proof.link
  );
  if (!filePath) return false;
  return !/placeholder|sample|dummy|temp|draft/i.test(filePath);
};

export const hasProofHistory = (proof = {}) => {
  const raw = proof.auditTrail ?? proof.history ?? proof.events;
  if (Array.isArray(raw)) return raw.length > 0;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length > 0;
    } catch {
      return false;
    }
  }
  return false;
};

export const isSyntheticEmptyProofRow = (proof = {}, poData = {}) => {
  const proofId = normalizeId(proof.id || proof.proofId);
  const idLooksLikePoPlaceholder = Boolean(proofId && getPoIds(poData).has(proofId));

  return Boolean(
    idLooksLikePoPlaceholder &&
    !hasUploadedProofFile(proof) &&
    !hasProofHistory(proof) &&
    !normalizeId(proof.filePath) &&
    !normalizeId(proof.proofFilePath)
  );
};

export const isEffectiveProofRow = (proof = {}, poData = {}) => {
  if (!proof) return false;
  if (isSyntheticEmptyProofRow(proof, poData)) return false;
  if (hasUploadedProofFile(proof)) return true;
  if (hasProofHistory(proof)) return true;

  const explicitPoId = normalizeProofPoId(proof);
  const isExplicitForThisPo = Boolean(explicitPoId && getPoIds(poData).has(explicitPoId));
  if (isExplicitForThisPo && poData.proofRequired === true) return true;

  return false;
};

const isDecorationOrProofResponsiblePo = (poData = {}) => {
  const supplierInfo = getSupplierInfo(poData);
  const typeValues = [poData.purchaseOrderType, supplierInfo.type].map((v) => String(v || '').trim());
  const decorationTypes = new Set([
    'Different_supplier_decoration',
    'Same_supplier_decoration',
    'decoration',
    'Other_service_art',
    'Combined_supplier_and_external_decoration',
  ]);

  if (poData.proofRequired === true) return true;
  if (typeValues.some((type) => decorationTypes.has(type))) return true;
  if (supplierInfo.decoratorId != null && String(supplierInfo.decoratorId).trim()) return true;
  if (Array.isArray(supplierInfo.charges) && supplierInfo.charges.some((charge) => charge?.decoratorId != null)) return true;

  return false;
};

const appliesToTargets = (proof = {}) => [
  ...arrayFrom(proof.appliesTo),
  ...arrayFrom(proof.applies_to),
  ...arrayFrom(proof.target),
  ...arrayFrom(proof.targets),
];

const targetMatchesPo = (target, poData) => {
  const poIds = getPoIds(poData);
  const poNumbers = getPoNumbers(poData);
  const decoratorIds = getDecoratorIds(poData);
  const decorationIds = getDecorationIds(poData);
  const lineIds = getLineIds(poData);

  if (typeof target === 'string' || typeof target === 'number') {
    const id = normalizeId(target);
    return poIds.has(id) || poNumbers.has(id) || decoratorIds.has(id) || decorationIds.has(id) || lineIds.has(id);
  }

  if (!target || typeof target !== 'object') return false;

  const targetPoIds = compactIds([target.poId, target.purchaseOrderId, target.purchase_order_id, target.POId, target.po_id]);
  const targetPoNumbers = compactIds([target.poNumber, target.purchaseOrderNumber, target.purchase_order_number]);
  const targetDecoratorIds = compactIds([target.decoratorId, target.DecoratorId, target.decorator_id]);
  const targetDecorationIds = compactIds([target.decorationId, target.DecorationId, target.decoration_id, target.locationId, target.location_id]);
  const targetLineIds = compactIds([target.orderItemId, target.OrderItemId, target.order_item_id, target.productId, target.ProductId, target.product_id, target.lineId, target.line_id]);

  return setsIntersect(targetPoIds, poIds) ||
    setsIntersect(targetPoNumbers, poNumbers) ||
    setsIntersect(targetDecoratorIds, decoratorIds) ||
    setsIntersect(targetDecorationIds, decorationIds) ||
    setsIntersect(targetLineIds, lineIds);
};

export const proofExplicitlyMatchesPo = (proof = {}, poData = {}) => {
  const proofPoId = normalizeProofPoId(proof);
  return Boolean(proofPoId && getPoIds(poData).has(proofPoId));
};

export const proofAppliesToCurrentPo = (proof = {}, poData = {}) =>
  appliesToTargets(proof).some((target) => targetMatchesPo(target, poData));

const legacyDecorationProofMatchesPo = (proof = {}, poData = {}) => {
  if (normalizeProofPoId(proof)) return false;
  const proofDecorationIds = getProofDecorationIds(proof);
  const proofDecoratorIds = getProofDecoratorIds(proof);
  const proofLineIds = getProofLineIds(proof);

  return (
    (proofDecorationIds.size > 0 && setsIntersect(proofDecorationIds, getDecorationIds(poData))) ||
    (proofDecoratorIds.size > 0 && setsIntersect(proofDecoratorIds, getDecoratorIds(poData))) ||
    (proofLineIds.size > 0 && setsIntersect(proofLineIds, getLineIds(poData)))
  );
};

export const resolveProofsForPurchaseOrder = (allProofs = [], poData = {}) => (
  (Array.isArray(allProofs) ? allProofs : []).filter((proof) => {
    if (!isEffectiveProofRow(proof, poData)) return false;
    if (proofExplicitlyMatchesPo(proof, poData)) return true;
    if (proofAppliesToCurrentPo(proof, poData)) return true;
    if (isDecorationOrProofResponsiblePo(poData)) {
      return legacyDecorationProofMatchesPo(proof, poData);
    }
    return false;
  })
);
